import React, { useEffect, useState, useRef, useCallback } from 'react';
import './PublicCADemo.css';
import { listArticles, getArticle, searchArticles } from './publicCaServices';
import Calender from './PublicCaCalender';

import shareIcon from '../assets/images/share.svg';

const PAGE_SIZE = 12;
const CURRENT_MONTH = ['january','february','march','april','may','june','july','august','september','october','november','december'][new Date().getMonth()];
const CURRENT_YEAR = new Date().getFullYear();

const SOCIAL_SHARES = [
  { name: 'Copy link', img: '/assets/images/copy-1.svg', action: (url, handleCopy) => handleCopy(url) },
  { name: 'WhatsApp', img: '/assets/images/whatsapp.svg', action: (url) => window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, '_blank') },
  { name: 'Telegram', img: '/assets/images/telegram.svg', action: (url) => window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}`, '_blank') },
  { name: 'Facebook', img: '/assets/images/facebook.svg', action: (url) => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank') },
  { name: 'LinkedIn', img: '/assets/images/linkdine.svg', action: (url) => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}`, '_blank') },
  { name: 'X', img: '/assets/images/x.svg', action: (url) => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, '_blank') },
];

function escHtml(s) {
  const d = document.createElement('div');
  d.textContent = s || '';
  return d.innerHTML;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
}

export default function PublicCADemo() {
  const [status, setStatus] = useState({ msg: 'Ready', type: 'loading' });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [activeKeyword, setActiveKeyword] = useState('');
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharePopupPos, setSharePopupPos] = useState({ top: 0, left: 0 });
  const [shareItemId, setShareItemId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [cachedItems, setCachedItems] = useState(null);
  const [calender, setCalender] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedMonths, setSelectedMonths] = useState([CURRENT_MONTH]);
  const [selectedYears, setSelectedYears] = useState([CURRENT_YEAR]);
  const [categories, setCategories] = useState([
 { "name": "All", "img": "assets/images/model-ans.svg" },
  { "name": "Economy", "img": "assets/currentaffirs/economy.svg" },
  { "name": "Geography", "img": "assets/currentaffirs/geography.svg" },
  { "name": "Polity", "img": "assets/currentaffirs/polity.svg" },
  { "name": "Environment/Disaster Management", "img": "assets/currentaffirs/environment.svg" },
  { "name": "Ethics", "img": "assets/currentaffirs/ethics.svg" },
  { "name": "IR/Security", "img": "assets/currentaffirs/IR.svg" },
  { "name": "History/Culture", "img": "assets/currentaffirs/history.svg" },
  { "name": "Governance", "img": "assets/currentaffirs/governance.svg" },
  { "name": "Society/Social Justice", "img": "assets/currentaffirs/social.svg" },
  { "name": "Science&Technology", "img": "assets/currentaffirs/science.svg" }
]);
  const [activeIndex, setActiveIndex] = useState(0);
  const filterDivRef = useRef(null);

  const isInitialMount = useRef(true);
  const isLoading = status.type === 'loading';
  const displayItems = searchTerm.trim() ? (searchResults || []) : items;
  const shareUrl = shareItemId ? `${window.location.origin}/currentaffairs/${shareItemId}` : '';

  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; loadArticles(0); return; }
    loadArticles(0);
  }, [filter, selectedMonths, selectedYears, activeKeyword]);

  function setStatusMsg(msg, type) { setStatus({ msg, type }); }

  async function loadArticles(s = 0) {
    setItems(Array(PAGE_SIZE).fill(null));
    setStatusMsg('Fetching articles...', 'loading');
    try {
      const kwArray = activeKeyword ? activeKeyword.split(/[&/]/).map(k => k.trim()).filter(Boolean) : [];
      const data = await listArticles({
        skip: s, limit: PAGE_SIZE, date_filter: 'all',
        keywords: kwArray, years: selectedYears,
        months: selectedMonths, read_filter: filter
      });
      setStatusMsg(`${data.total} articles found — page ${data.page}`, 'ok');
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(Math.floor(s / PAGE_SIZE) + 1);
    } catch (e) {
      setStatusMsg(e.message, 'err');
      setItems([]);
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        setCachedItems(items);
        performSearch(searchTerm.trim());
      } else if (cachedItems) {
        setItems(cachedItems);
        setSearchResults(null);
        setIsSearching(false);
        setCachedItems(null);
      } else {
        setSearchResults(null);
        setIsSearching(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!showSharePopup) return;
    const close = () => setShowSharePopup(false);
    const scrollDiv = document.querySelector('.PublicCA-parent-div');
    scrollDiv?.addEventListener('scroll', close, { passive: true });
    window.addEventListener('click', close);
    return () => {
      scrollDiv?.removeEventListener('scroll', close);
      window.removeEventListener('click', close);
    };
  }, [showSharePopup]);

  const performSearch = async (term) => {
    setIsSearching(true);
    setItems([]);
    setStatusMsg(`Searching for "${term}"...`, 'loading');
    try {
      const response = await searchArticles([term], 10);
      const results = response.matches || response.items || [];
      setSearchResults(results);
      setStatusMsg(`${results.length} results for "${term}"`, 'ok');
    } catch (e) {
      setStatusMsg(`Search failed: ${e.message}`, 'err');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  async function openDetail(id) {
    setShowDetail(true);
    setDetail(null);
    try {
      setDetail(await getArticle(id));
    } catch (e) {
      setDetail({ error: e.message });
    }
  }

  function closeDetail() { setShowDetail(false); setDetail(null); setShowSharePopup(false); }

  const modalRef = useRef(null);

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const card = modalRef.current;
    if (!card) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = card.offsetWidth;
    const startH = card.offsetHeight;
    const suppressClick = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
    window.addEventListener('click', suppressClick, true);
    const onMove = (ev) => {
      const w = Math.max(400, startW + (ev.clientX - startX));
      const h = Math.max(300, startH + (ev.clientY - startY));
      card.style.width = w + 'px';
      card.style.height = h + 'px';
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setTimeout(() => window.removeEventListener('click', suppressClick, true), 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  function handleSourceClick(source) {
    if (source) {
      window.open(source.startsWith('http') ? source : `https://${source}`, '_blank', 'noopener,noreferrer');
    }
  }

  function handleCopy(url) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => alert('Failed to copy link'));
  }

  function handleCategoryClick(name, index) {
    setActiveKeyword(name === 'All' ? '' : name);
    setActiveIndex(index);
  }

  function scrollFilterBy(delta) {
    filterDivRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }

  function openShare(e, id) {
    e.stopPropagation();
    const rect = e.target.getBoundingClientRect();
    setSharePopupPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setShareItemId(id);
    setShowSharePopup(true);
    setCopied(false);
  }

  function renderMarkdownArticle(md) {
    if (!md) return '';
    const spanMatch = md.match(/^<span[^>]*>(.*?)<\/span>/);
    let evalTag = '';
    if (spanMatch) { evalTag = `<div class="PublicCA-eval-tag">${spanMatch[1]}</div>`; md = md.substring(spanMatch[0].length).trim(); }
    const lines = md.split('\n');
    let html = evalTag;
    const dotColors = ['PublicCA-dot-green','PublicCA-dot-orange','PublicCA-dot-red','PublicCA-dot-blue','PublicCA-dot-purple'];
    let currentSection = null; let dotIdx = 0;
    for (let raw of lines) {
      const trimmed = raw.trim();
      if (!trimmed || trimmed === '---') continue;
      const headingMatch = trimmed.match(/^#{1,6}\s+(.+)/);
      if (headingMatch) {
        if (currentSection === 'takeaway') html += '</div>';
        if (currentSection === 'list') html += '</ul></div>';
        if (currentSection === 'strategic') html += '</div>';
        currentSection = null;
        const title = headingMatch[1].trim();
        const isKeyTakeaways = title.toLowerCase().includes('key takeaway');
        const isStrategic = title.toLowerCase().includes('strategic');
        if (isKeyTakeaways) { currentSection = 'takeaway'; dotIdx = 0; html += `<div class="PublicCA-key-takeaways"><div class="PublicCA-article-section-title"> ${escHtml(title)}</div>`; }
        else if (isStrategic) { currentSection = 'strategic'; html += `<div class="PublicCA-article-section"><div class="PublicCA-article-section-title">${escHtml(title)}</div>`; }
        else { currentSection = 'list'; html += `<div class="PublicCA-article-section"><div class="PublicCA-article-section-title">${escHtml(title)}</div><ul class="PublicCA-section-list">`; }
        continue;
      }
      if (currentSection === 'takeaway') { let clean = trimmed.replace(/^>\s*/, '').trim(); if (!clean) continue; clean = clean.replace(/^[^\p{L}\p{N}*]*/u, ''); if (!clean) continue; html += `<div class="PublicCA-takeaway-item"><span class="PublicCA-takeaway-dot ${dotColors[dotIdx % dotColors.length]}"></span><span>${clean.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')}</span></div>`; dotIdx++; }
      else if (currentSection === 'strategic') { let clean = trimmed.replace(/^>\s*/, '').trim(); if (!clean) continue; html += `<div class="PublicCA-strategic-item">${clean.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')}</div>`; }
      else if (currentSection === 'list') { let clean = trimmed.replace(/^>\s*/, '').replace(/^[-•*]\s*/, '').trim(); if (!clean) continue; html += `<li>${clean.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')}</li>`; }
      else { html += `<p>${trimmed.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')}</p>`; }
    }
    if (currentSection === 'takeaway') html += '</div>';
    if (currentSection === 'list') html += '</ul></div>';
    if (currentSection === 'strategic') html += '</div>';
    return html;
  }

  return (
    <div className="PublicCA-root">
      <header className="PublicCA-header">
        <div className="PublicCA-header-inner">
          <div className="PublicCA-logo">Current Affairs</div>
        </div>
      </header>

      <div className="PublicCA-content">
        <div className="PublicCA-toolbar">
          <label className="PublicCA-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search topics..."
              aria-label="Search topics"
            />
          </label>
          <div className="PublicCA-filter-group">
            <Calender
              setCalender={setCalender}
              calender={calender}
              setSelectedYears={setSelectedYears}
              setSelectedMonths={setSelectedMonths}
              selectedYears={selectedYears}
              selectedMonths={selectedMonths}
              newloader={isLoading}
              categories={categories}
              activeKeyword={activeKeyword}
              activeIndex={activeIndex}
              setActiveKeyword={setActiveKeyword}
              setActiveIndex={setActiveIndex}
            />
          </div>
        </div>

        {/* <div className="PublicCA-fil">
          <div>
            <button className="PublicCA-filter-arrow-right" onClick={() => scrollFilterBy(-100)}>
              <img src="/assets/images/arrow2.svg" className="PublicCA-img-arrow" style={{ marginLeft: '0px' }} alt="scroll" />
            </button>
          </div>
          <div className="PublicCA-filter-div" ref={filterDivRef}>
            {categories.map((category, index) => (
              <button
                key={index}
                className={`PublicCA-filter-button ${activeIndex === index ? 'PublicCA-active' : ''}`}
                onClick={() => handleCategoryClick(category.name, index)}
                disabled={isLoading}
              >
                <img src={category.img} alt="icon" />
                <span className="PublicCA-filt-text">{category.name}</span>
              </button>
            ))}
          </div>
          <div>
            <button className="PublicCA-filter-arrow" onClick={() => scrollFilterBy(100)}>
              <img src="/assets/images/arrow2.svg" className="PublicCA-img-arrow" style={{ marginLeft: '0px' }} alt="scroll" />
            </button>
          </div>
        </div> */}

        <div className="PublicCA-main-div">
          <div className="PublicCA-current-affairsdiv">
            <div className="PublicCA-parent-div">
              {displayItems.length === 0 && !isLoading && !isSearching && (!searchTerm.trim() || searchResults !== null) && (
                <div className="PublicCA-no-data"><span>No articles</span></div>
              )}
              {isSearching && Array.from({ length: 4 }).map((_, idx) => (
                <div className="PublicCA-skeleton PublicCA-skeleton-large" key={idx} />
              ))}
              {!isSearching && displayItems.map((item, idx) =>
                item ? (
                  <div key={item.id || idx} className="PublicCA-card-anchor" onClick={() => openDetail(item.id)}>
                    <div className="PublicCA-news-card" style={{ position: 'relative' }}>
                      {item.is_read && <div className="PublicCA-news-image-read" />}
                      <div className="PublicCA-card-image-wrapper">
                        <img
                          className="PublicCA-news-image"
                          src={item.images?.[0]?.image_url || '/assets/images/noimage.jpeg'}
                          alt={item.topic || ''}
                          onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/noimage.jpeg'; }}
                        />
                      </div>
                      <div className="PublicCA-news-content-img">
                        <p className="PublicCA-news-title">{item.topic ? item.topic.substring(0, 50) + '...' : ''}</p>
                        <p className="PublicCA-news-date">{item.ca_created_date ? formatDate(item.ca_created_date) : ''}</p>
                        <p className="PublicCA-news-text">{item.content ? item.content.split(' ').slice(0, 150).join(' ') + '...' : ''}</p>
                      </div>
                      <div className="PublicCA-card-footer">
                        {/* <span className="PublicCA-read-time">read {item.view_count || 0} times</span> */}
                        <div className="PublicCA-bookmark-icon">
                          <img
                            src={`https://www.google.com/s2/favicons?sz=64&domain_url=${item.source || ''}`}
                            alt="source"
                            className="PublicCA-source-icon"
                            title="View Source"
                            onClick={(e) => { e.stopPropagation(); handleSourceClick(item.source); }}
                          />
                          {/* <img
                            src={shareIcon}
                            alt="share"
                            className="PublicCA-share-icon"
                            title="Share"
                            onClick={(e) => openShare(e, item.id)}
                          /> */}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="PublicCA-skeleton PublicCA-skeleton-large" key={idx} />
                )
              )}
            </div>

            {!searchTerm.trim() && (
              <div className="PublicCA-pagination-wrapper">
                {page > 1 && <button className="PublicCA-btn PublicCA-btn-sm PublicCA-btn-ghost" onClick={() => loadArticles((page-2)*PAGE_SIZE)}>Prev</button>}
                <span className="PublicCA-page-info">Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
                {page < Math.ceil(total / PAGE_SIZE) && <button className="PublicCA-btn PublicCA-btn-sm PublicCA-btn-ghost" onClick={() => loadArticles(page*PAGE_SIZE)}>Next</button>}
              </div>
            )}
          </div>
        </div>
      </div>

      {showSharePopup && shareItemId && (
        <>
          <div className="PublicCA-share-backdrop" onClick={() => setShowSharePopup(false)} />
          <div
            className="PublicCA-share-popup"
            style={{ position: 'absolute', top: sharePopupPos.top, left: sharePopupPos.left, zIndex: 9999 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="PublicCA-share-buttons">
              {SOCIAL_SHARES.map((s) => (
                <div key={s.name} onClick={() => s.action(shareUrl, handleCopy)}>
                  <img src={s.img} alt={s.name} width={30} height={30} />
                </div>
              ))}
            </div>
            {copied && <p className="PublicCA-copy-msg">Link copied!</p>}
          </div>
        </>
      )}

      {showDetail && (
        <div className="PublicCA-detail-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}>
          <div className="PublicCA-detail-card" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="PublicCA-detail-header">
              <div className="PublicCA-detail-close" onClick={closeDetail}><img src="assets/images/close.svg" alt=""  width={20} height={20}/></div>
              {detail && !detail.error && (
                <div>
                  <div className="PublicCA-detail-title">{detail.topic}</div>
                  <div className="PublicCA-detail-meta">
                    <div className="PublicCA-detail-date">
                      <span><img src="/assets/images/current-affairs-calender.svg" alt="date" width={14} height={14} /></span>
                      <span>{new Date(detail.ca_created_date).toLocaleDateString()}</span>
                    </div>
                    <a href={detail.source} target="_blank" rel="noreferrer" className="PublicCA-detail-source-icon">
                      <img src={`https://www.google.com/s2/favicons?sz=64&domain_url=${detail.source}`} alt="source" width={16} height={16} />
                    </a>
                    {/* <img
                      src={shareIcon}
                      alt="share"
                      className="PublicCA-detail-share-icon"
                      title="Share"
                      onClick={(e) => openShare(e, detail?.id)}
                    /> */}
                  </div>
                </div>
              )}
            </div>
            <div className="PublicCA-detail-body">
              {!detail && <div className="PublicCA-skeleton PublicCA-skeleton-xl" />}
              {detail?.error && <div className="PublicCA-empty-state"><p>{detail.error}</p></div>}
              {detail && !detail.error && (
                <div>
                  {/* {detail.images?.length > 0 && (
                    <div className="PublicCA-detail-images">
                      {detail.images.map((i, ix) => <img key={ix} src={i.image_url} alt="" />)}
                    </div>
                  )} */}
                  <div className="PublicCA-article-body" dangerouslySetInnerHTML={{
                    __html: detail.highlights?.length
                      ? renderMarkdownArticle(detail.highlights[0].highlight_text)
                      : `<p>${escHtml(detail.content)}</p>`
                  }} />
                </div>
              )}
            </div>
            <div className="PublicCA-resize-handle" onMouseDown={handleResizeMouseDown} onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
}
